import { createContext, useCallback, useContext, useState } from "react";
import { monthCodeFromOffset } from "@package/grid-utils";
import { generateNKeysBetween } from "fractional-indexing";
import { nanoid } from "nanoid";

import { useOptionalActivePageId, useInsertPageProducts, usePageProducts } from "../../../../data";
import { useGrid } from "../../../market-grid/stores";
import { compareIdx } from "../../../utils";
import { columnWidth } from "../../../globals";
import type { PageProductsFragmentFragment } from "../../../../__generated__/gql/graphql";
import { useUserId } from "../../../../context/auth/hooks";

export const useTargetCell = () => {
  const pageProducts = usePageProducts(useOptionalActivePageId());
  const [grid] = useGrid();

  return useCallback(
    (productId: string, row: number) => {
      if (!grid) return;
      const products = pageProducts.data?.filter((p) => p.product_id === productId);
      if (!products?.length) return;
      for (const product of products) {
        const rowId = monthCodeFromOffset(row);
        const column = grid.getColumn(product.grid_id ?? product.id);
        if (!column) return;

        setTimeout(() => {
          grid.flashCells({
            rowNodes: [grid.getRowNode(rowId)].filter(Boolean),
            columns: [column],
            flashDuration: 500,
            fadeDuration: 500,
          });
        }, 100);
      }
    },
    [grid, pageProducts.data],
  );
};

export const tryCatch = <T>(fn: () => T, defaultValue: T): T => {
  try {
    return fn();
  } catch {
    return defaultValue;
  }
};

export type SelectedFormula = {
  product: string;
  formula: string;
  row: number;
  /** Stable AST path for the clicked token, used to disambiguate identical references. */
  path: string;
  /**
   * X position of the parent card's center, relative to the formula container.
   * The expanded formula is shifted right so its last token sits under this X,
   * clamped so it never overflows the container's left or right edge.
   */
  arrowX?: number;
};
export const useSelectedFormula = () => useState<SelectedFormula | null>(null);

export type InsertCurveActions = {
  insertNextToSelected: (productId: string) => void;
  insertAtEnd: (productId: string) => void;
  isProductOnPage: (productId: string) => boolean;
};

export const InsertCurveContext = createContext<InsertCurveActions | null>(null);

export const useInsertCurveActions = () => useContext(InsertCurveContext);

/**
 * True when the cell whose precedents are being traced is sourced from EOD —
 * either by status (`eod`) or by `artis_type === "eod"`. EOD has no LUT or
 * username, so the modal must not surface those for any precedent token in
 * this context (per WEB-1740).
 */
export const RootEodSourceContext = createContext<boolean>(false);
export const useIsRootEodSource = () => useContext(RootEodSourceContext);

export const useInsertPrecedentCurve = (): InsertCurveActions => {
  const insert = useInsertPageProducts();
  const userId = useUserId();
  const pageId = useOptionalActivePageId();
  const pageProducts = usePageProducts(pageId);
  const [grid] = useGrid();

  const createColumn = useCallback(
    (productId: string, idx: string, resolvedPageId: string) => {
      const newColId = nanoid();
      return {
        id: newColId,
        grid_id: newColId,
        idx,
        product_id: productId,
        user_id: userId,
        page_id: resolvedPageId,
        column_type: "product",
        column_width: columnWidth,
        column_field_selector: undefined,
        decimal_places: undefined,
        thousands_separator: undefined,
      } satisfies PageProductsFragmentFragment;
    },
    [userId],
  );

  const isProductOnPage = useCallback(
    (productId: string) => !!pageProducts.data?.some((p) => p.product_id === productId),
    [pageProducts.data],
  );

  const insertNextToSelected = useCallback(
    (productId: string) => {
      if (!pageId) return;
      if (isProductOnPage(productId)) return;
      const sorted = pageProducts.data ? [...pageProducts.data].sort(compareIdx) : undefined;
      if (!sorted?.length) return;

      const focusedColumn = grid?.getFocusedCell()?.column;
      const focusedColId = focusedColumn?.getColId();
      const currentIdx = sorted.findIndex((x) => (x.grid_id ?? x.id) === focusedColId);
      const afterIdx = currentIdx >= 0 ? currentIdx : sorted.length - 1;

      const idx = generateNKeysBetween(sorted[afterIdx].idx, sorted[afterIdx + 1]?.idx, 1)[0];
      insert.mutate([createColumn(productId, idx, pageId)]);
    },
    [pageId, pageProducts.data, grid, insert, createColumn, isProductOnPage],
  );

  const insertAtEnd = useCallback(
    (productId: string) => {
      if (!pageId) return;
      if (isProductOnPage(productId)) return;
      const sorted = pageProducts.data?.sort(compareIdx);
      if (!sorted?.length) return;

      const lastIdx = sorted[sorted.length - 1].idx;
      const idx = generateNKeysBetween(lastIdx, null, 1)[0];
      insert.mutate([createColumn(productId, idx, pageId)]);
    },
    [pageId, pageProducts.data, insert, createColumn, isProductOnPage],
  );

  return { insertNextToSelected, insertAtEnd, isProductOnPage };
};
